<template>
    <div class="shop-management">
        <!-- 顶部数据统计卡片 -->
        <div class="data-overview">
            <el-card shadow="hover" v-for="(stat, index) in stats" :key="index" class="stat-card">
                <div class="stat-content">
                    <div class="stat-icon" :style="{ background: stat.color }">
                        <el-icon :size="24">
                            <component :is="stat.icon" />
                        </el-icon>
                    </div>
                    <div class="stat-info">
                        <div class="stat-value">{{ stat.value }}</div>
                        <div class="stat-label">{{ stat.label }}</div>
                    </div>
                </div>
            </el-card>
        </div>

        <!-- 搜索和操作区域 -->
        <div class="action-bar">
            <el-input v-model="searchQuery" placeholder="输入商品名称或ID搜索" clearable class="search-input"
                @input="handleSearchInput">
                <template #append>
                    <el-button :icon="Search" @click="handleSearchInput" />
                </template>
            </el-input>

            <el-radio-group v-model="stockFilter" size="default" class="stock-filter">
                <el-radio-button label="all">全部库存</el-radio-button>
                <el-radio-button label="low">库存紧张</el-radio-button>
                <el-radio-button label="soldout">已售罄</el-radio-button>
                <el-radio-button label="unlimited">不限库存</el-radio-button>
            </el-radio-group>

            <div class="action-buttons">
                <!-- 批量操作按钮组 -->
                <el-button-group>
                    <el-button 
                        :type="batchAction === 'shelf' ? 'success' : 'default'"
                        :icon="Top" 
                        :disabled="selectedRows.length === 0 || batchLoading"
                        @click="batchStatusChange('1')"
                    >
                        批量上架
                    </el-button>
                    <el-button 
                        :type="batchAction === 'unshelf' ? 'info' : 'default'"
                        :icon="Bottom" 
                        :disabled="selectedRows.length === 0 || batchLoading"
                        @click="batchStatusChange('0')"
                    >
                        批量下架
                    </el-button>
                    <el-button 
                        type="danger" 
                        :icon="Delete" 
                        :disabled="selectedRows.length === 0 || batchLoading"
                        @click="batchDelete"
                    >
                        批量删除
                    </el-button>
                </el-button-group>
                <el-button :icon="Refresh" @click="getShopList" :loading="loading">刷新</el-button>
            </div>
        </div>

        <!-- 商品表格 -->
        <div class="shop-table">
            <el-table :data="pagedData" border stripe :loading="loading" height="calc(100vh - 310px)"
                style="width: 100%" @selection-change="handleSelectionChange">
                <el-table-column type="selection" width="45" />
                <el-table-column prop="shopid" label="商品ID" width="85" align="center" />
                <el-table-column prop="shopname" label="商品名称" min-width="120" show-overflow-tooltip />
                <el-table-column prop="shopprice" label="价格" width="100" align="right">
                    <template #default="scope">
                        <span>{{ scope.row.shopprice }} </span>
                        <el-tag size="mini" type="info">积分</el-tag>
                    </template>
                </el-table-column>
                <el-table-column prop="shoptime" label="制作周期" width="100" align="center">
                    <template #default="scope">
                        <span>{{ scope.row.shoptime }} </span>
                        <el-tag size="mini" effect="plain">天</el-tag>
                    </template>
                </el-table-column>
                <el-table-column prop="shopsale" label="状态" width="100" align="center">
                    <template #default="scope">
                        <el-tag :type="scope.row.shopsale === '1' ? 'success' : 'danger'" effect="light"
                            :class="{ 'pulse': scope.row.shopsale === '1' }">
                            {{ scope.row.shopsale === '1' ? '可生产' : '不可生产' }}
                        </el-tag>
                    </template>
                </el-table-column>
                <el-table-column prop="stock" label="库存" width="110" align="center" sortable
                    :sort-method="sortByStock">
                    <template #default="scope">
                        <el-tag v-if="isUnlimitedStock(scope.row.stock)" type="info" effect="plain" size="small">
                            不限
                        </el-tag>
                        <el-tag v-else-if="Number(scope.row.stock) <= 0" type="danger" effect="dark" size="small">
                            已售罄
                        </el-tag>
                        <el-tag v-else :type="Number(scope.row.stock) <= 5 ? 'warning' : 'success'" effect="light"
                            size="small">
                            剩 {{ scope.row.stock }}
                        </el-tag>
                    </template>
                </el-table-column>
                <el-table-column prop="virtual" label="类型" width="90" align="center">
                    <template #default="scope">
                        <el-tag :type="scope.row.virtual === '1' ? 'primary' : 'success'" size="small">
                            {{ scope.row.virtual === '1' ? '电子礼物' : '实物' }}
                        </el-tag>
                    </template>
                </el-table-column>
                <el-table-column label="商品图片" width="130" align="center">
                    <template #default="scope">
                        <div class="product-image-container">
                            <el-image v-if="scope.row.shopimg" :src="getImageUrl(scope.row.shopimg)" fit="cover"
                                class="product-image" :preview-src-list="[getImageUrl(scope.row.shopimg)]"
                                :preview-teleported="true">
                                <template #error>
                                    <div class="image-error">
                                        <el-icon><Picture /></el-icon>
                                    </div>
                                </template>
                            </el-image>
                            <div v-if="scope.row.shopimg" class="image-preview-hint">
                                <el-icon><ZoomIn /></el-icon>
                            </div>
                            <div v-else class="no-image">
                                <el-icon><Picture /></el-icon>
                            </div>
                        </div>
                    </template>
                </el-table-column>
                <el-table-column label="操作" fixed="right" width="180">
                    <template #default="scope">
                        <div class="action-buttons-group">
                            <el-tooltip content="查看详情" placement="top">
                                <el-button type="primary" :icon="View" circle @click="openDetail(scope.row)" />
                            </el-tooltip>
                            <el-tooltip content="编辑" placement="top">
                                <el-button type="warning" :icon="Edit" circle @click="openEdit(scope.row)" />
                            </el-tooltip>
                            <el-tooltip content="删除" placement="top">
                                <el-button type="danger" :icon="Delete" circle @click="handleDelete(scope.row.shopid)" />
                            </el-tooltip>
                            <el-tooltip :content="scope.row.shopsale === '1' ? '下架' : '上架'" placement="top">
                                <el-button :type="scope.row.shopsale === '1' ? 'info' : 'success'"
                                    :icon="scope.row.shopsale === '1' ? Bottom : Top" circle
                                    @click="toggleStatus(scope.row)" />
                            </el-tooltip>
                        </div>
                    </template>
                </el-table-column>
            </el-table>
        </div>

        <!-- 分页组件 -->
        <div class="pagination" v-if="filteredData.length > 0">
            <el-pagination background v-model:current-page="pagination.current" v-model:page-size="pagination.size"
                :page-sizes="[10, 20, 50, 100]" layout="total, sizes, prev, pager, next, jumper"
                :total="filteredData.length" @size-change="handlePageChange" @current-change="handlePageChange" />
        </div>

        <!-- 详情弹窗 -->
        <el-dialog title="商品详情" v-model="detailVisible" width="700px" custom-class="detail-dialog"
            :close-on-click-modal="false">
            <div class="detail-content">
                <div class="image-section">
                    <el-image v-if="currentShop.shopimg" :src="getImageUrl(currentShop.shopimg)" fit="cover"
                        class="detail-image" :preview-src-list="[getImageUrl(currentShop.shopimg)]">
                        <template #error>
                            <div class="image-error">
                                <el-icon><Picture /></el-icon>
                                <p>图片加载失败</p>
                            </div>
                        </template>
                    </el-image>
                    <div v-else class="image-placeholder">
                        <el-icon><Picture /></el-icon>
                        <p>暂无图片</p>
                    </div>
                </div>
                <div class="info-section">
                    <el-descriptions :column="1" border>
                        <el-descriptions-item label="商品ID">
                            <span class="text-bold">{{ currentShop.shopid }}</span>
                        </el-descriptions-item>
                        <el-descriptions-item label="商品名称">
                            <span class="text-bold">{{ currentShop.shopname }}</span>
                        </el-descriptions-item>
                        <el-descriptions-item label="积分价格">
                            <span class="price">{{ currentShop.shopprice }} 积分</span>
                        </el-descriptions-item>
                        <el-descriptions-item label="制作周期">
                            <span>{{ currentShop.shoptime }} 天</span>
                        </el-descriptions-item>
                        <el-descriptions-item label="商品状态">
                            <el-tag :type="currentShop.shopsale === '1' ? 'success' : 'danger'" class="status-tag">
                                {{ currentShop.shopsale === '1' ? '可生产' : '不可生产' }}
                            </el-tag>
                        </el-descriptions-item>
                        <el-descriptions-item label="库存数量">
                            <el-tag v-if="isUnlimitedStock(currentShop.stock)" type="info" effect="plain">不限库存</el-tag>
                            <el-tag v-else-if="Number(currentShop.stock) <= 0" type="danger" effect="dark">已售罄</el-tag>
                            <span v-else class="stock-value">剩余 {{ currentShop.stock }} 件</span>
                        </el-descriptions-item>
                        <el-descriptions-item label="创建时间" v-if="currentShop.createTime">
                            <span>{{ formatDate(currentShop.createTime) }}</span>
                        </el-descriptions-item>
                    </el-descriptions>
                </div>
            </div>
        </el-dialog>

        <!-- 编辑弹窗 -->
        <el-dialog :title="isEditMode ? '编辑商品' : '新增商品'" v-model="editVisible" width="600px" top="8vh"
            custom-class="edit-dialog" :close-on-click-modal="false">
            <el-form :model="editForm" :rules="editRules" ref="editFormRef" label-width="100px">
                <el-form-item label="商品ID" v-if="isEditMode">
                    <el-input v-model="editForm.shopid" disabled />
                </el-form-item>
                <el-form-item label="商品名称" prop="shopname">
                    <el-input v-model="editForm.shopname" placeholder="请输入商品名称" clearable />
                </el-form-item>
                <el-form-item label="积分价格" prop="shopprice">
                    <el-input-number v-model="editForm.shopprice" :min="1" :precision="0" class="price-input" />
                    <span class="unit">积分</span>
                </el-form-item>
                <el-form-item label="制作周期" prop="shoptime">
                    <el-input-number v-model="editForm.shoptime" :min="1" :max="90" :precision="0" class="time-input" />
                    <span class="unit">天</span>
                </el-form-item>
                <el-form-item label="库存数量" prop="stock">
                    <div class="stock-field">
                        <el-switch v-model="editStockUnlimited" active-text="不限库存" inactive-text="限量库存"
                            @change="handleEditStockModeChange" />
                        <el-input-number v-if="!editStockUnlimited" v-model="editForm.stock" :min="1" :max="9999"
                            :precision="0" :step="1" controls-position="right" class="stock-input" />
                        <span v-else class="unit">不限库存时用户可无限次兑换</span>
                    </div>
                    <div class="stock-hint">限量库存可填 1-9999（已售罄的商品也可在此补货）</div>
                </el-form-item>
                <el-form-item label="商品状态" prop="shopsale">
                    <el-radio-group v-model="editForm.shopsale">
                        <el-radio :label="1">可生产</el-radio>
                        <el-radio :label="0">不可生产</el-radio>
                    </el-radio-group>
                </el-form-item>
                <el-form-item label="礼物类型" prop="virtual">
                    <el-radio-group v-model="editForm.virtual">
                        <el-radio :label="'0'">实物</el-radio>
                        <el-radio :label="'1'">电子礼物</el-radio>
                    </el-radio-group>
                </el-form-item>
                <el-form-item label="电子礼物文件" v-if="editForm.virtual === '1'" prop="virtualFiles">
                    <div class="virtual-file-upload">
                        <el-upload
                            :auto-upload="false"
                            :limit="3"
                            :on-change="handleEditVirtualFileChange"
                            :on-exceed="handleEditExceed"
                            :on-remove="handleEditVirtualFileRemove"
                            :file-list="editVirtualFileList"
                            multiple
                            drag
                        >
                            <el-icon class="el-icon--upload"><UploadFilled /></el-icon>
                            <div class="el-upload__text">
                                拖拽文件到此处或 <em>点击上传</em>
                            </div>
                            <template #tip>
                                <div class="el-upload__tip">最多3个文件（可替换现有文件）</div>
                            </template>
                        </el-upload>
                    </div>
                </el-form-item>
                <el-form-item label="商品图片">
                    <div class="image-uploader">
                        <el-upload :auto-upload="false" :show-file-list="false" :on-change="handleImageChange"
                            accept="image/*">
                            <el-button type="primary" :icon="Upload">选择图片</el-button>
                        </el-upload>
                        <div class="tip-text">支持 JPG、PNG、GIF、WebP、BMP 格式</div>
                        <div class="image-preview-container" v-if="editForm.imagePreview">
                            <div class="image-preview">
                                <el-image :src="editForm.imagePreview" fit="cover" />
                                <div class="delete-overlay" @click="removeImage">
                                    <el-icon><Delete /></el-icon>
                                </div>
                            </div>
                        </div>
                        <div v-else-if="!editForm.imagePreview && !editForm.shopimg" class="image-placeholder">
                            <el-icon><Picture /></el-icon>
                            <p>暂无图片</p>
                        </div>
                    </div>
                </el-form-item>
            </el-form>
            <template #footer>
                <el-button @click="editVisible = false">取消</el-button>
                <el-button type="primary" @click="submitEdit" :loading="editLoading">
                    {{ isEditMode ? '保存修改' : '确认添加' }}
                </el-button>
            </template>
        </el-dialog>
    </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed, nextTick } from 'vue';
import {
    Search, Refresh, Edit, Delete, Upload, Picture,
    View, Top, Bottom, ZoomIn, UploadFilled
} from '@element-plus/icons-vue';
import axios from 'axios';
import { ElMessage, ElMessageBox } from 'element-plus';
import { resolveImageUrl } from '../../../util/imageUrl';

// 商品数据
const tableData = ref([]);                // 所有商品原始数据
const loading = ref(false);
const detailVisible = ref(false);
const editVisible = ref(false);
const editLoading = ref(false);
const currentShop = reactive({});
const selectedRows = ref([]);
const searchQuery = ref('');
// 库存筛选：all 全部 / low 库存紧张(1-5) / soldout 已售罄 / unlimited 不限库存
const stockFilter = ref('all');
const isEditMode = ref(false);
const batchLoading = ref(false);           // 批量操作加载状态
const batchAction = ref('');                // 当前批量操作类型（用于按钮高亮）

// 分页设置（前端分页）
const pagination = reactive({
    current: 1,
    size: 10
});

// 统计数据
// 第三张卡原来是「今日新增」，但 shop 表没有时间字段，createTime 恒为 undefined → 永远显示 0，
// 这里改成有真实数据支撑的「库存预警」（已售罄或仅剩 1-5 件）
const stats = ref([
    { icon: 'Goods', value: '0', label: '商品总数', color: '#FF6B6B' },
    { icon: 'Box', value: '0', label: '可生产商品', color: '#4ECDC4' },
    { icon: 'Warning', value: '0', label: '库存预警', color: '#E6A23C' }
]);

// 编辑表单数据
const editForm = reactive({
    shopid: '',
    shopname: '',
    shopprice: 0,
    shoptime: 1,
    shopsale: 1,
    stock: 1,
    virtual: '0',
    imagePreview: '',
    shopimg: null
});

// 库存开关：true(不限库存) / false(限量库存，1-9999)
const STOCK_UNLIMITED = -1;
const editStockUnlimited = ref(false);
let editLastLimitedStock = 1;

// 0（已售罄）也要能编辑补货，这里只把 null/undefined/-1 视为不限库存
const isUnlimitedStock = (value) => {
    if (value === null || value === undefined || value === '') return true;
    return Number(value) < 0;
};

const handleEditStockModeChange = (unlimited) => {
    if (unlimited) {
        if (Number(editForm.stock) > 0) editLastLimitedStock = Number(editForm.stock);
        editForm.stock = STOCK_UNLIMITED;
    } else {
        editForm.stock = editLastLimitedStock > 0 ? editLastLimitedStock : 1;
    }
};

// 排序：不限库存排最后，其余按数量升序（库存紧张的排前面）
const sortByStock = (a, b) => {
    const av = isUnlimitedStock(a.stock) ? Number.MAX_SAFE_INTEGER : Number(a.stock);
    const bv = isUnlimitedStock(b.stock) ? Number.MAX_SAFE_INTEGER : Number(b.stock);
    return av - bv;
};

const editFormRef = ref(null);
const editVirtualRawFiles = ref([]);
const editVirtualFileList = ref([]);

// 表单验证规则（保持不变）
const editRules = {
    shopname: [
        { required: true, message: '请输入商品名称', trigger: 'blur' },
        { min: 2, max: 50, message: '长度在 2 到 50 个字符', trigger: 'blur' }
    ],
    shopprice: [
        { required: true, message: '请输入积分价格', trigger: 'blur' },
        { type: 'number', min: 1, message: '积分价格必须大于0', trigger: 'blur' }
    ],
    shoptime: [
        { required: true, message: '请输入制作周期', trigger: 'blur' },
        { type: 'number', min: 1, max: 30, message: '制作周期1-30天', trigger: 'blur' }
    ],
    stock: [
        {
            validator: (rule, value, callback) => {
                if (editStockUnlimited.value) return callback();
                const n = Number(value);
                // 编辑时允许填 0（售罄但要保留记录），补货范围 0-9999
                if (!Number.isInteger(n) || n < 0 || n > 9999) {
                    return callback(new Error('库存数量需为 0-9999 的整数，或选择不限库存'));
                }
                callback();
            },
            trigger: 'blur'
        }
    ]
};

// ---------- 计算属性：过滤和分页 ----------
const filteredData = computed(() => {
    const query = searchQuery.value.toLowerCase().trim();
    return tableData.value.filter(item => {
        // 关键词过滤
        if (query) {
            const matchKeyword =
                String(item.shopid).includes(query) ||
                String(item.shopname || '').toLowerCase().includes(query);
            if (!matchKeyword) return false;
        }
        // 库存过滤
        const stock = Number(item.stock);
        switch (stockFilter.value) {
            case 'low':
                return !isUnlimitedStock(item.stock) && stock > 0 && stock <= 5;
            case 'soldout':
                return !isUnlimitedStock(item.stock) && stock <= 0;
            case 'unlimited':
                return isUnlimitedStock(item.stock);
            default:
                return true;
        }
    }).map(item => ({
        // 兼容旧库缺失 stock 列的情况：统一按「不限库存」展示，避免出现 undefined/NaN
        ...item,
        stock: isUnlimitedStock(item.stock) ? STOCK_UNLIMITED : Number(item.stock)
    }));
});

const pagedData = computed(() => {
    const start = (pagination.current - 1) * pagination.size;
    const end = start + pagination.size;
    return filteredData.value.slice(start, end);
});

// 搜索输入时重置到第一页
const handleSearchInput = () => {
    pagination.current = 1;
};

// 切换库存筛选后同样回到第一页，否则会停在一个空页上
watch(stockFilter, () => {
    pagination.current = 1;
});

// 分页变化时无需额外操作，pagedData 自动更新
const handlePageChange = () => {};

// ---------- 数据获取 ----------
const getShopList = async () => {
    loading.value = true;
    try {
        // 移除分页参数，获取全部商品
        // 后端 /adminapi/shop/list 目前不接受任何分页参数，所以这里不再发送无效的 size
        const response = await axios.get('/adminapi/shop/list');

        // 假设返回数据格式为 { results: [...] } 或直接数组
        tableData.value = response.data.results || response.data || [];
        pagination.current = 1; // 重置到第一页
        updateStats(tableData.value);
    } catch (error) {
        console.error('获取商品列表失败:', error);
        ElMessage.error('网络请求失败，请重试');
    } finally {
        loading.value = false;
    }
};

// 更新统计数据
const updateStats = (data) => {
    if (!data || data.length === 0) {
        stats.value = [
            { icon: 'Goods', value: '0', label: '商品总数', color: '#FF6B6B' },
            { icon: 'Box', value: '0', label: '可生产商品', color: '#4ECDC4' },
            { icon: 'Warning', value: '0', label: '库存预警', color: '#E6A23C' }
        ];
        return;
    }

    const totalCount = data.length;
    // 修复：shopsale可能是字符串'1'或数字1，统一处理
    const producibleCount = data.filter(item => String(item.shopsale) === '1' || item.shopsale === 1).length;
    // 库存预警：已售罄（0）或仅剩 1-5 件；不限库存（-1）不计入
    const stockWarningCount = data.filter(item => {
        if (isUnlimitedStock(item.stock)) return false;
        const stock = Number(item.stock) || 0;
        return stock <= 5;
    }).length;

    stats.value = [
        { icon: 'Goods', value: totalCount.toString(), label: '商品总数', color: '#FF6B6B' },
        { icon: 'Box', value: producibleCount.toString(), label: '可生产商品', color: '#4ECDC4' },
        { icon: 'Warning', value: stockWarningCount.toString(), label: '库存预警', color: '#E6A23C' }
    ];
};

// 表格选择行
const handleSelectionChange = (selection) => {
    selectedRows.value = selection;
};

// ---------- 单条操作（保持不变）----------
const openDetail = (row) => {
    Object.assign(currentShop, row);
    detailVisible.value = true;
};

const openEdit = (row) => {
    isEditMode.value = true;
    resetEditForm();
    editForm.shopid = row.shopid;
    editForm.shopname = row.shopname;
    editForm.shopprice = Number(row.shopprice);
    editForm.shoptime = Number(row.shoptime);
    // 确保shopsale转换为数字类型，因为radio-group使用数字label
    editForm.shopsale = Number(row.shopsale);
    // 库存回填：-1 走「不限库存」开关，0（已售罄）保留数字以便补货
    const unlimited = isUnlimitedStock(row.stock);
    editStockUnlimited.value = unlimited;
    editForm.stock = unlimited ? STOCK_UNLIMITED : Number(row.stock);
    editLastLimitedStock = unlimited ? 1 : Math.max(1, Number(row.stock));
    editForm.virtual = row.virtual || '0';
    editForm.imagePreview = row.shopimg ? getImageUrl(row.shopimg) : '';
    editVirtualRawFiles.value = [];
    editVirtualFileList.value = [];
    editVisible.value = true;
};

const resetEditForm = () => {
    editForm.shopid = '';
    editForm.shopname = '';
    editForm.shopprice = 0;
    editForm.shoptime = 1;
    editForm.shopsale = 1;
    editForm.stock = 1;
    editStockUnlimited.value = false;
    editLastLimitedStock = 1;
    editForm.imagePreview = '';
    editForm.shopimg = null;
    nextTick(() => {
        editFormRef.value?.clearValidate();
    });
};

const getImageUrl = resolveImageUrl;

const handleImageChange = (file) => {
    const isImage = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/bmp'].includes(file.raw.type);
    if (!isImage) {
        ElMessage.error('仅支持上传图片文件（JPG、PNG、GIF、WebP、BMP）');
        return false;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
        editForm.imagePreview = e.target.result;
    };
    reader.readAsDataURL(file.raw);
    editForm.shopimg = file.raw;
};

const removeImage = () => {
    editForm.imagePreview = '';
    editForm.shopimg = null;
};

// 编辑时虚拟文件处理
const handleEditVirtualFileChange = (file) => {
    editVirtualRawFiles.value.push(file.raw);
};

const handleEditVirtualFileRemove = (file) => {
    const index = editVirtualRawFiles.value.findIndex(f => f.name === file.name && f.size === file.size);
    if (index > -1) editVirtualRawFiles.value.splice(index, 1);
};

const handleEditExceed = () => {
    ElMessage.warning('最多只能上传3个文件');
};

const submitEdit = async () => {
    // ... 原有代码不变 ...
    try {
        await editFormRef.value.validate();
        editLoading.value = true;
        const formData = new FormData();
        formData.append('shopname', editForm.shopname);
        formData.append('shopprice', editForm.shopprice);
        formData.append('shoptime', editForm.shoptime);
        formData.append('shopsale', editForm.shopsale);
        formData.append('virtual', editForm.virtual);
        // -1 = 不限库存；否则为 0-9999 的剩余数量
        formData.append('stock', editStockUnlimited.value ? STOCK_UNLIMITED : editForm.stock);
        if (editForm.shopimg) {
            formData.append('file', editForm.shopimg);
        }
        // 添加虚拟礼物文件
        if (editForm.virtual === '1' && editVirtualRawFiles.value.length > 0) {
            editVirtualRawFiles.value.forEach(f => {
                formData.append('virtualFiles', f);
            });
        }
        if (isEditMode.value) {
            formData.append('shopid', editForm.shopid);
            await axios.post('/adminapi/shop/amend', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
        } else {
            await axios.post('/adminapi/shop/add', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
        }
        ElMessage.success(isEditMode.value ? '商品修改成功' : '商品添加成功');
        editVisible.value = false;
        getShopList();
    } catch (error) {
        console.error('提交失败:', error);
        ElMessage.error(error.response?.data?.message || '操作失败');
    } finally {
        editLoading.value = false;
    }
};

const handleDelete = async (shopid) => {
    try {
        await ElMessageBox.confirm(
            '此操作将永久删除该商品，是否继续？',
            '警告',
            {
                confirmButtonText: '确认删除',
                cancelButtonText: '取消',
                type: 'warning',
                center: true
            }
        );
        loading.value = true;
        await axios.delete(`/adminapi/shop/${shopid}`);
        await getShopList();
        ElMessage.success('商品已删除');
    } catch (error) {
        if (error !== 'cancel') {
            console.error('删除商品失败:', error);
            ElMessage.error(error.response?.data?.message || '删除失败');
        }
    } finally {
        loading.value = false;
    }
};

const toggleStatus = async (row) => {
    try {
        const newStatus = row.shopsale === '1' ? '0' : '1';
        const action = newStatus === '1' ? '上架' : '下架';
        await ElMessageBox.confirm(
            `确定要${action}该商品吗?`,
            '提示',
            { confirmButtonText: '确定', cancelButtonText: '取消' }
        );
        loading.value = true;
        await axios.post('/adminapi/shop/status', {
            shopid: row.shopid,
            shopsale: newStatus
        });
        await getShopList(); // 刷新列表
        ElMessage.success(`商品已${action}`);
    } catch (error) {
        if (error !== 'cancel') {
            console.error('操作失败:', error);
            ElMessage.error(error.response?.data?.message || '操作失败');
        }
    } finally {
        loading.value = false;
    }
};

// ---------- 批量操作 ----------
const batchStatusChange = async (targetStatus) => {
    if (selectedRows.value.length === 0) return;
    const action = targetStatus === '1' ? '上架' : '下架';
    try {
        await ElMessageBox.confirm(
            `确定要将选中的 ${selectedRows.value.length} 个商品${action}吗？`,
            '批量操作确认',
            { confirmButtonText: '确定', cancelButtonText: '取消', type: 'warning' }
        );
        // 按钮高亮判断的是 'shelf'/'unshelf'，这里原来存的是中文动作名，导致高亮永远不生效
        batchAction.value = targetStatus === '1' ? 'shelf' : 'unshelf';
        batchLoading.value = true;

        // 原来是一个商品一个请求（并发 N 个），改成一条批量接口
        const shopids = selectedRows.value.map(row => row.shopid);
        const response = await axios.post('/adminapi/shop/batchstatus', {
            shopids,
            shopsale: targetStatus
        });

        if (response.data && response.data.code !== 200) {
            throw new Error(response.data.message || '批量操作失败');
        }
        const affected = response.data?.data?.affected ?? shopids.length;
        ElMessage.success(`批量${action}成功，共处理 ${affected} 个商品`);
        await getShopList(); // 刷新列表
    } catch (error) {
        if (error !== 'cancel') {
            console.error('批量操作失败:', error);
            ElMessage.error('批量操作失败，请重试');
        }
    } finally {
        batchLoading.value = false;
        batchAction.value = '';
    }
};

const batchDelete = async () => {
    if (selectedRows.value.length === 0) return;
    try {
        await ElMessageBox.confirm(
            `确定要永久删除选中的 ${selectedRows.value.length} 个商品吗？此操作不可恢复！`,
            '批量删除确认',
            { confirmButtonText: '确定删除', cancelButtonText: '取消', type: 'warning' }
        );
        batchAction.value = 'delete';
        batchLoading.value = true;

        // 同样改走批量接口，勾选 100 个商品也只发一个请求
        const shopids = selectedRows.value.map(row => row.shopid);
        const response = await axios.post('/adminapi/shop/batchstatus', {
            shopids,
            action: 'delete'
        });

        if (response.data && response.data.code !== 200) {
            throw new Error(response.data.message || '批量删除失败');
        }
        const affected = response.data?.data?.affected ?? shopids.length;
        ElMessage.success(`批量删除成功，共删除 ${affected} 个商品`);
        await getShopList();
    } catch (error) {
        if (error !== 'cancel') {
            console.error('批量删除失败:', error);
            ElMessage.error('批量删除失败，请重试');
        }
    } finally {
        batchLoading.value = false;
        batchAction.value = '';
    }
};

// 日期格式化（不变）
const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString();
};

onMounted(() => {
    getShopList();
});

// 监听表格数据变化，更新统计数据（已有 watch，但需导入 watch）
import { watch } from 'vue';
watch(tableData, (newData) => {
    updateStats(newData);
}, { deep: true });

</script>

<style scoped lang="scss">
/* 样式保持不变，仅需添加批量按钮组样式 */
.action-buttons {
    display: flex;
    gap: 10px;
    align-items: center;

    .el-button-group {
        margin-right: 8px;
    }
}

/* 其他原有样式不变，此处省略以节省篇幅，实际使用时保留完整样式 */
</style>

<style scoped lang="scss">
.shop-management {
    padding: 20px;
    background-color: #f8fafc;
    border-radius: 8px;
    min-height: calc(100vh - 60px);
}

.data-overview {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: 16px;
    margin-bottom: 20px;

    .stat-card {
        border-radius: 8px;
        transition: all 0.3s ease;
        border: none;

        &:hover {
            transform: translateY(-3px);
            box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
        }
    }

    .stat-content {
        display: flex;
        align-items: center;
        padding: 10px;
    }

    .stat-icon {
        width: 48px;
        height: 48px;
        border-radius: 10px;
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        margin-right: 15px;
    }

    .stat-value {
        font-size: 24px;
        font-weight: bold;
        color: #2d3748;
        line-height: 1.2;
    }

    .stat-label {
        font-size: 13px;
        color: #718096;
    }
}

.action-bar {
    display: flex;
    justify-content: space-between;
    margin-bottom: 20px;
    gap: 15px;
    flex-wrap: wrap;

    .search-input {
        width: 300px;

        :deep(.el-input-group__append) {
            background-color: var(--el-color-primary);
            color: white;
            border-top-left-radius: 0;
            border-bottom-left-radius: 0;

            &:hover {
                opacity: 0.9;
            }
        }
    }

    .action-buttons {
        display: flex;
        gap: 10px;
    }
}

.shop-table {
    margin-bottom: 20px;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
    background: white;

    .product-image-container {
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        position: relative;

        &:hover .image-preview-hint {
            opacity: 1;
        }
    }

    .image-preview-hint {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        background: rgba(0, 0, 0, 0.6);
        color: white;
        border-radius: 50%;
        width: 32px;
        height: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
        opacity: 0;
        transition: opacity 0.3s ease;
        pointer-events: none;
        z-index: 1;

        .el-icon {
            font-size: 18px;
        }
    }

    .product-image {
        width: 80px;
        height: 60px;
        border-radius: 4px;
        display: block;
        cursor: pointer;
        transition: all 0.3s ease;
        border: 2px solid transparent;

        &:hover {
            transform: scale(1.1);
            border-color: var(--el-color-primary);
            box-shadow: 0 4px 12px rgba(64, 158, 255, 0.3);
        }
    }

    .no-image,
    .image-error {
        width: 80px;
        height: 60px;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: #f5f7fa;
        border-radius: 4px;
        color: #c0c4cc;
    }

    .action-buttons-group {
        display: flex;
        gap: 8px;
        flex-wrap: wrap;

        .el-button {
            transition: all 0.3s ease;

            &:hover {
                transform: scale(1.1);
            }
        }
    }
}

.pagination {
    display: flex;
    justify-content: flex-end;
    padding: 10px 0;
}

/* 详情弹窗样式 */
.detail-dialog {
    border-radius: 12px;

    .detail-content {
        display: flex;
        gap: 24px;

        .image-section {
            flex: 0 0 250px;

            .detail-image,
            .image-error,
            .image-placeholder {
                width: 100%;
                height: 250px;
                border-radius: 8px;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
            }

            .image-error,
            .image-placeholder {
                background: #f5f7fa;
                color: #909399;
                border: 1px dashed #dcdfe6;

                .el-icon {
                    font-size: 50px;
                    margin-bottom: 10px;
                }

                p {
                    margin: 0;
                    font-size: 14px;
                }
            }
        }

        .info-section {
            flex: 1;

            :deep(.el-descriptions) {
                .el-descriptions__header {
                    display: none;
                }

                .el-descriptions__body {
                    background: transparent;

                    .el-descriptions-item__cell {
                        padding: 12px 0;

                        .el-descriptions-item__label {
                            color: #606266;
                            font-weight: 500;
                            width: 100px;
                        }

                        .text-bold {
                            font-weight: 600;
                        }

                        .price {
                            color: var(--el-color-primary);
                            font-weight: bold;
                        }

                        .status-tag {
                            font-weight: bold;
                        }
                    }
                }
            }
        }
    }
}

/* 库存筛选与库存字段 */
.stock-filter {
    flex-shrink: 0;
}

.stock-field {
    display: flex;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;
}

.stock-input {
    width: 150px;
}

.stock-hint {
    width: 100%;
    margin-top: 4px;
    font-size: 12px;
    color: #909399;
    line-height: 1.5;
}

.stock-value {
    font-weight: 600;
    color: #409eff;
}

/* 编辑弹窗样式 */
.edit-dialog {
    border-radius: 12px;

    .el-form-item {
        margin-bottom: 20px;
    }

    .price-input,
    .time-input {
        width: 130px;
    }

    .stock-input {
        width: 150px;
    }

    .unit {
        margin-left: 10px;
        color: #606266;
    }

    .image-uploader {
        .tip-text {
            font-size: 12px;
            color: #909399;
            margin-top: 5px;
        }

        .image-preview-container {
            margin-top: 15px;
        }

        .image-preview {
            position: relative;
            width: 180px;
            height: 140px;
            border-radius: 6px;
            overflow: hidden;
            border: 1px dashed #dcdfe6;

            .el-image {
                width: 100%;
                height: 100%;
            }

            .delete-overlay {
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                background: rgba(0, 0, 0, 0.5);
                display: flex;
                align-items: center;
                justify-content: center;
                color: white;
                cursor: pointer;
                opacity: 0;
                transition: opacity 0.3s;

                &:hover {
                    opacity: 1;
                }

                .el-icon {
                    font-size: 24px;
                }
            }
        }

        .image-placeholder {
            width: 180px;
            height: 140px;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            background: #f5f7fa;
            border: 1px dashed #dcdfe6;
            border-radius: 6px;
            color: #909399;

            .el-icon {
                font-size: 40px;
                margin-bottom: 10px;
            }

            p {
                margin: 0;
                font-size: 14px;
            }
        }
    }
}

.pulse {
    animation: pulse 1.5s infinite;
}

@keyframes pulse {
    0% {
        box-shadow: 0 0 0 0 rgba(103, 194, 58, 0.5);
    }

    70% {
        box-shadow: 0 0 0 8px rgba(103, 194, 58, 0);
    }

    100% {
        box-shadow: 0 0 0 0 rgba(103, 194, 58, 0);
    }
}

/* 响应式调整 */
@media (max-width: 992px) {
    .action-bar {
        flex-direction: column;

        .search-input {
            width: 100%;
        }
    }

    .detail-content {
        flex-direction: column !important;
    }
}

@media (max-width: 768px) {
    .data-overview {
        grid-template-columns: 1fr;
    }

    .action-buttons-group {
        flex-wrap: wrap !important;
    }

    .el-table {
        min-width: 800px;
        overflow-x: auto;
    }
}
</style>